import { Note as TonalNote } from 'tonal';

/**
 * Pitch representation.
 *
 * MIDI integers are canonical for computation; `Spelled` carries the written
 * identity (letter + accidental), which MIDI cannot express. G♯4 and A♭4 are the
 * same MIDI number and different notes, and half the theory curriculum turns on
 * that difference, so the two are never conflated.
 */

export type Letter = 'C' | 'D' | 'E' | 'F' | 'G' | 'A' | 'B';
export const LETTERS: readonly Letter[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

/** Semitones above C for each natural letter. */
const LETTER_SEMITONES: Record<Letter, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

/** Position of each letter in the diatonic sequence starting at C. */
const LETTER_STEPS: Record<Letter, number> = { C: 0, D: 1, E: 2, F: 3, G: 4, A: 5, B: 6 };

export interface Spelled {
  letter: Letter;
  /** -2 double flat … +2 double sharp. */
  alter: number;
  /** Scientific pitch notation: middle C is C4. */
  octave: number;
}

export type PitchClass = number; // 0..11

export const MIN_MIDI = 0;
export const MAX_MIDI = 127;

/* -------------------------------------------------------------- conversions */

export function midiOf(spelled: Spelled): number {
  return (spelled.octave + 1) * 12 + LETTER_SEMITONES[spelled.letter] + spelled.alter;
}

export function pitchClassOf(spelled: Spelled): PitchClass {
  return ((midiOf(spelled) % 12) + 12) % 12;
}

export function pitchClassOfMidi(midi: number): PitchClass {
  return ((midi % 12) + 12) % 12;
}

/** Diatonic step index, counting C0 as 0. Used for interval numbers and staff position. */
export function diatonicStep(spelled: Spelled): number {
  return spelled.octave * 7 + LETTER_STEPS[spelled.letter];
}

/** Builds a Spelled from a letter/alter/octave, validating the accidental range. */
export function spelled(letter: Letter, alter: number, octave: number): Spelled {
  if (!Number.isInteger(alter) || alter < -2 || alter > 2) {
    throw new Error(`Accidental out of range: ${alter}`);
  }
  if (!Number.isInteger(octave)) throw new Error(`Octave must be an integer: ${octave}`);
  return { letter, alter, octave };
}

/* ----------------------------------------------------------------- parsing */

const NOTE_RE = /^([A-Ga-g])(#{1,2}|b{1,2}|x|##|)(-?\d+)?$/;

/**
 * Parses tonal-style names: `C4`, `F#3`, `Bb`, `Cx4` (double sharp), `Ebb2`.
 * Without an octave the note is placed in `defaultOctave`.
 */
export function parseNote(name: string, defaultOctave = 4): Spelled {
  const match = NOTE_RE.exec(name.trim());
  if (match === null) throw new Error(`Cannot parse note: "${name}"`);
  const [, rawLetter, acc, oct] = match;
  const letter = (rawLetter as string).toUpperCase() as Letter;

  let alter = 0;
  if (acc === '#') alter = 1;
  else if (acc === '##' || acc === 'x') alter = 2;
  else if (acc === 'b') alter = -1;
  else if (acc === 'bb') alter = -2;

  return { letter, alter, octave: oct === undefined ? defaultOctave : Number.parseInt(oct, 10) };
}

/** Tonal-compatible name (`F#4`, `Bb3`). Used when handing values to tonal. */
export function toTonal(s: Spelled): string {
  const acc = s.alter >= 0 ? '#'.repeat(s.alter) : 'b'.repeat(-s.alter);
  return `${s.letter}${acc}${s.octave}`;
}

/** Tonal-compatible pitch class, with no octave. */
export function toTonalPc(s: Spelled): string {
  const acc = s.alter >= 0 ? '#'.repeat(s.alter) : 'b'.repeat(-s.alter);
  return `${s.letter}${acc}`;
}

export function fromTonal(name: string): Spelled {
  const n = TonalNote.get(name);
  if (n.empty || n.letter === undefined) throw new Error(`tonal could not parse "${name}"`);
  return {
    letter: n.letter as Letter,
    alter: n.alt ?? 0,
    octave: n.oct ?? 4,
  };
}

/* ---------------------------------------------------------------- spelling */

/** Sharp-side default spelling for each pitch class. */
const SHARP_SPELLING: readonly [Letter, number][] = [
  ['C', 0],
  ['C', 1],
  ['D', 0],
  ['D', 1],
  ['E', 0],
  ['F', 0],
  ['F', 1],
  ['G', 0],
  ['G', 1],
  ['A', 0],
  ['A', 1],
  ['B', 0],
];

/** Flat-side default spelling for each pitch class. */
const FLAT_SPELLING: readonly [Letter, number][] = [
  ['C', 0],
  ['D', -1],
  ['D', 0],
  ['E', -1],
  ['E', 0],
  ['F', 0],
  ['G', -1],
  ['G', 0],
  ['A', -1],
  ['A', 0],
  ['B', -1],
  ['B', 0],
];

export interface SpellingContext {
  /** Signed key-signature count: +n sharps, -n flats. Drives the default side. */
  accidentals?: number;
  /** Letters that belong to the current key, preferred when they fit. */
  diatonicLetters?: readonly Letter[];
  /** Ascending lines prefer sharps, descending prefer flats. */
  direction?: 'up' | 'down';
}

/**
 * Chooses a written spelling for a MIDI number.
 *
 * Order of preference: a letter from the key's diatonic set, then the side
 * implied by the key signature, then the direction of travel for chromatic
 * passing notes (sharps ascending, flats descending).
 */
export function spell(midi: number, ctx: SpellingContext = {}): Spelled {
  const pc = pitchClassOfMidi(midi);

  if (ctx.diatonicLetters !== undefined && ctx.diatonicLetters.length > 0) {
    // Prefer a spelling whose letter is in the key, with the smallest accidental.
    const candidates = enharmonicsOfPitchClass(pc)
      .filter((c) => ctx.diatonicLetters?.includes(c.letter) === true)
      .sort((a, b) => Math.abs(a.alter) - Math.abs(b.alter));
    const best = candidates[0];
    if (best !== undefined) return withOctaveForMidi(best.letter, best.alter, midi);
  }

  const flatSide =
    (ctx.accidentals !== undefined && ctx.accidentals < 0) ||
    (ctx.accidentals === undefined && ctx.direction === 'down');

  const table = flatSide ? FLAT_SPELLING : SHARP_SPELLING;
  const entry = table[pc] as [Letter, number];
  return withOctaveForMidi(entry[0], entry[1], midi);
}

/** Picks the octave so that (letter, alter, octave) lands on exactly `midi`. */
function withOctaveForMidi(letter: Letter, alter: number, midi: number): Spelled {
  const octave = Math.floor((midi - LETTER_SEMITONES[letter] - alter) / 12) - 1;
  return { letter, alter, octave };
}

/** Every spelling of a pitch class with |alter| <= 2, ordered by simplicity. */
export function enharmonicsOfPitchClass(pc: PitchClass): { letter: Letter; alter: number }[] {
  const out: { letter: Letter; alter: number }[] = [];
  for (const letter of LETTERS) {
    // The alteration that turns this letter into the target pitch class,
    // normalized into -6..+5 so we can reject anything beyond a double accidental.
    let alter = pc - LETTER_SEMITONES[letter];
    alter = ((alter + 6) % 12) - 6;
    if (Math.abs(alter) <= 2) out.push({ letter, alter });
  }
  return out.sort((a, b) => Math.abs(a.alter) - Math.abs(b.alter));
}

/** Every spelling of the same sounding pitch, excluding the input. */
export function enharmonics(s: Spelled): Spelled[] {
  const midi = midiOf(s);
  return enharmonicsOfPitchClass(pitchClassOf(s))
    .map((c) => withOctaveForMidi(c.letter, c.alter, midi))
    .filter((c) => !(c.letter === s.letter && c.alter === s.alter));
}

export function isEnharmonic(a: Spelled, b: Spelled): boolean {
  return midiOf(a) === midiOf(b);
}

/** Same written note ignoring octave. */
export function sameSpelling(a: Spelled, b: Spelled): boolean {
  return a.letter === b.letter && a.alter === b.alter;
}

/* --------------------------------------------------------------- utilities */

export function transposeOctaves(s: Spelled, octaves: number): Spelled {
  return { ...s, octave: s.octave + octaves };
}

export function compareMidi(a: Spelled, b: Spelled): number {
  return midiOf(a) - midiOf(b);
}

/** Sorts ascending by sounding pitch, then by diatonic step for enharmonic ties. */
export function sortNotes(notes: readonly Spelled[]): Spelled[] {
  return [...notes].sort((a, b) => midiOf(a) - midiOf(b) || diatonicStep(a) - diatonicStep(b));
}

export function isInRange(midi: number, low: number, high: number): boolean {
  return midi >= low && midi <= high;
}

/** Semitone distance between two notes, positive when `b` is higher. */
export function semitonesBetween(a: Spelled, b: Spelled): number {
  return midiOf(b) - midiOf(a);
}

/**
 * Shifts a note by whole octaves until it fits the range, or returns null when
 * no octave placement fits. Used by instrument auto-fit.
 */
export function fitToRange(s: Spelled, low: number, high: number): Spelled | null {
  if (high < low) throw new Error('fitToRange: inverted range');
  let candidate = s;
  let guard = 0;
  while (midiOf(candidate) < low && guard < 12) {
    candidate = transposeOctaves(candidate, 1);
    guard += 1;
  }
  while (midiOf(candidate) > high && guard < 24) {
    candidate = transposeOctaves(candidate, -1);
    guard += 1;
  }
  return isInRange(midiOf(candidate), low, high) ? candidate : null;
}
